import {
  useGetTourQuery,
  useGetTourSearchLinksResultsQuery,
} from '@carboncopyco/data';
import {
  BrowserIcon,
  DurationOutlineIcon,
  LinkIcon,
  TrendingOutlineIcon,
} from '@carboncopyco/icons';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DiscardChangesModal, useSpeakerNotes } from '../../SpeakerNotes';
import { InfoPageSidebar, type TabButtons } from '../InfoPageSidebar';
import { SpeakerNotesIcon } from '../SpeakerNotesIcon';
import { useTourPageProps } from './TourPageContext';
import type { TourPageTab } from './types';

export const TourPageSidebar = () => {
  const [showDiscardModal, setShowDiscardModal] = useBoolean();
  const {
    goBack,
    tourId,
    organizationId,
    rootPath,
    workspaceId,
    noteValue,
    setNoteValue,
    pendingNavigationRoute,
    setPendingNavigationRoute,
  } = useTourPageProps();

  const { data: tour, isLoading: isLoadingTour } = useGetTourQuery({
    workspaceId,
    organizationId,
    id: Number(tourId),
  });
  const { data: shares, isLoading: isLoadingShares } =
    useGetTourSearchLinksResultsQuery({
      workspaces: [workspaceId],
      organizationId,
      id: String(tourId),
      active: true,
    });
  const flags = useFlags();
  const tabButtons = useMemo<TabButtons<TourPageTab>>(() => {
    const buttons = {
      overview: {
        label: 'Overview',
        icon: <BrowserIcon size={18} />,
      },
      links: {
        label:
          isLoadingShares || !shares?.metadata.totalItems
            ? 'Shares'
            : `Shares (${shares?.metadata.totalItems})`,
        icon: <LinkIcon size={18} />,
      },
      activity: {
        label: 'Activity',
        icon: <TrendingOutlineIcon size={18} />,
      },
      history: {
        label: 'History',
        icon: <DurationOutlineIcon size={18} />,
      },
    };
    return {
      ...buttons,
      ...(flags.speakerNotes
        ? {
            'speaker-notes': {
              label: 'Speaker notes',
              icon: (
                <SpeakerNotesIcon
                  organizationId={organizationId}
                  entityType="tour"
                  entityId={String(tourId)}
                />
              ),
            },
          }
        : {}),
    };
  }, [tourId, organizationId, flags, isLoadingShares, shares]);
  const navigate = useNavigate();
  const { note: savedNote } = useSpeakerNotes({
    entityType: 'tour',
    entityId: String(tourId),
  });
  const { tabId: currentTabId } = useParams();
  const hasUnsavedChanges = savedNote
    ? savedNote.content !== noteValue
    : !!noteValue;
  const handleChange = (tabId: string) => {
    const route = `${rootPath}/${tabId}`;
    if (hasUnsavedChanges) {
      setPendingNavigationRoute(route);
      setShowDiscardModal.setTrue();
      return;
    }
    navigate(route);
  };

  return (
    <>
      <InfoPageSidebar<TourPageTab>
        handleChange={
          currentTabId === 'speaker-notes' ? handleChange : undefined
        }
        tabButtons={tabButtons}
        screenshot={tour?.image}
        goBack={() => goBack(workspaceId)}
        rootPath={rootPath}
        isOverlayTour={tour && !tour.image}
        isLoading={isLoadingTour}
      />
      {showDiscardModal && (
        <DiscardChangesModal
          onDiscard={() => {
            setNoteValue(savedNote?.content);
            setShowDiscardModal.setFalse();
            navigate(pendingNavigationRoute);
          }}
          onCancel={setShowDiscardModal.setFalse}
        />
      )}
    </>
  );
};
